import { AxiosError } from 'axios'
import {
	buildLamenessChartMock,
	getLamenessChartMockMode,
} from 'components/cattles/lamenessPage/shared/helper/lamenessChartMock'
import { initialChartInfo } from 'components/cattles/lamenessPage/shared/processes'
import {
	aggregateDotsByMonthAverage,
	isDateRangeLongerThanMonths,
} from 'components/shared/processes/chartDateAggregation'
import {
	alignDotsToTimestamps,
	buildChartAxisTimestamps,
} from 'components/shared/processes/chartNoDataSeries'
import { ILamenessGraphResponse } from 'models/ICattlesData'
import { useCallback, useEffect, useMemo, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'
import { fetchLightGroupList } from 'store/groupsSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'

type DateRangeType = {
	startDate: number
	endDate: number
}

const useLamenessInfoForChart = (
	dateRange: DateRangeType,
	activeFilter?: string | number | null,
) => {
	const [dotsInfo, setDotsInfo] = useState(initialChartInfo)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [error, setError] = useState<AxiosError>(null)

	const { startDate, endDate } = dateRange

	const shouldAggregateMonthly = useMemo(
		() => isDateRangeLongerThanMonths(dateRange, 3),
		[dateRange],
	)

	const dispatch = useAppDispatch()

	const processServerData = useCallback(
		(res: ILamenessGraphResponse, aggregateMonthly: boolean) => {
			const timestamps = buildChartAxisTimestamps(
				startDate,
				endDate,
				aggregateMonthly,
			)
			const aggregatedLines = res.lines.map(line => ({
				...line,
				dots: aggregateMonthly
					? aggregateDotsByMonthAverage(line.dots)
					: line.dots,
			}))

			setDotsInfo(prevDots =>
				prevDots.map(el => {
					const line = aggregatedLines.find(l => l.score === el.score)
					const dots = line?.dots ?? []
					return {
						...el,
						category: alignDotsToTimestamps(dots, timestamps, aggregateMonthly),
						timestamp: timestamps,
						segmentValue: dots.reduce(
							(sum, num) => sum + (Number.isFinite(num.value) ? num.value : 0),
							0,
						),
					}
				}),
			)
		},
		[endDate, startDate],
	)

	const { lightGroupList } = useAppSelector(state => state.groupsReducer)

	useEffect(() => {
		setIsLoading(true)
		setError(null)

		if (!lightGroupList) {
			dispatch(fetchLightGroupList())
		}

		const mockMode = getLamenessChartMockMode()
		if (mockMode) {
			processServerData(
				buildLamenessChartMock(
					startDate,
					endDate,
					shouldAggregateMonthly,
					mockMode,
				),
				shouldAggregateMonthly,
			)
			setIsLoading(false)
			return
		}

		CattlesApiService.getLamenessGraph(
			startDate,
			endDate,
			!activeFilter || activeFilter === 'all' ? undefined : activeFilter,
		)
			.then(res => {
				processServerData(res, shouldAggregateMonthly)
			})
			.catch(err => {
				setError(err)
			})
			.finally(() => {
				setIsLoading(false)
			})
	}, [
		startDate,
		endDate,
		activeFilter,
		shouldAggregateMonthly,
		dispatch,
		lightGroupList,
		processServerData,
	])

	return {
		dotsInfo,
		isLoading,
		error,
		lightGroupList,
	}
}

export default useLamenessInfoForChart
